import type { Handle } from 'remix/ui'

/** The OKB mark's single path, verbatim from okb-web's icon asset. */
const OKB_MARK_PATH =
  'M53.7999 0.610195C40.3824 8.84142 33.087 9.0354 19.1764 1.53072C16.5064 0.0901933 16.5604 0.00217557 18.2119 3.10635C26.6963 19.0536 25.9164 22.0208 7.02779 45.6601C4.39347 48.9571 0.232267 54.3594 0.00893153 54.7727C-0.0277045 54.8405 0.0564265 54.9089 0.115319 54.8592L0.201794 54.7862C0.74991 54.3235 9.68408 47.2329 12.238 45.2336C32.8965 29.061 36.9219 28.399 54.0556 38.3554C54.2974 38.4959 54.5725 38.2329 54.443 37.9851L53.4383 36.0627C46.1728 22.162 46.2993 15.2937 54.0682 1.85576C55.348 -0.357618 55.3515 -0.341596 53.7999 0.610195ZM106.934 0.991605C93.6276 8.79895 87.12 8.96572 73.5811 1.84573C70.1898 0.0622058 70.2949 -0.242383 72.6835 4.44477C81.1314 21.023 80.5436 22.2784 46.3953 60.5794C45.6945 61.3655 45.0331 62.1039 44.9257 62.2201C42.2698 65.0941 26.6049 82.6935 21.2857 88.7791C14.8925 96.0935 9.56547 102.485 9.86441 102.482C10.0201 102.481 15.4493 98.0098 17.9488 95.8244C26.4022 88.4336 33.2382 82.2546 38.5637 77.1906C39.4123 76.3838 41.7089 74.2094 43.6673 72.3589C45.6254 70.5084 51.8333 64.4342 57.4622 58.8607C89.1864 27.4489 89.0909 27.49 106.741 37.6949C108.972 38.9849 108.905 39.0659 107.606 36.6416C100.044 22.5218 100.081 15.4078 107.789 2.04628C109.226 -0.444478 109.276 -0.38271 106.934 0.991605ZM108.38 55.6992C103.87 58.2982 99.3917 60.2665 95.6249 61.3055C94.3624 61.6537 93.0587 61.8299 91.7491 61.8294L89.9088 61.8287C88.2166 61.828 86.5328 61.5906 84.9064 61.1235C81.4794 60.1395 76.8509 58.0919 72.625 55.6907C71.0852 54.8159 71.1005 54.7484 72.3456 56.9305C81.8051 73.5099 81.1718 76.0495 61.32 101.132C55.3742 108.645 54.1964 110.202 54.6707 109.921C54.8282 109.827 58.3418 107.045 62.4787 103.739C87.5939 83.6634 90.9258 82.8421 107.976 92.5253C110.234 93.8083 110.224 93.8104 109.33 92.2324C100.964 77.4858 101.047 70.6717 109.773 55.6276C110.368 54.6017 110.277 54.6063 108.38 55.6992Z'

interface BrandMarkProps {
  size?: number
  className?: string
  /** Visual variant for different surfaces. Kept for API compatibility with
   * the upstream mascot art; all variants render the same neutral mark. */
  variant?: 'mark' | 'camera' | 'timeline' | 'share' | 'icon'
  /**
   * Home LCP art. Renders a same-size spacer; the visible mark lives in
   * index.html `#boot-hero` so first paint is not gated on the SPA bundle.
   */
  priority?: boolean
}

export function BrandMark(handle: Handle<BrandMarkProps>) {
  return () => {
    const { size = 56, className, priority = false } = handle.props

    if (priority) {
      return (
        <div
          className={className}
          style={{ width: size, height: size }}
          aria-hidden="true"
        />
      )
    }

    // The OKB mark (apps/web/public/images/logo/icon-dark.svg), inlined so
    // the app ships zero brand image assets and the boot hero in index.html
    // can render the identical path before any JS runs. Drawn in
    // `currentColor` rather than the asset's black, so one path serves both
    // themes (white on the dark theme, ink on light) — the same trick
    // okb-web plays with --brand-logo-filter.
    return (
      <svg
        className={className}
        width={size}
        height={size}
        viewBox="-14 -14 138 138"
        aria-hidden="true"
        focusable="false"
      >
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          fill="currentColor"
          d={OKB_MARK_PATH}
        />
      </svg>
    )
  }
}
